import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { IonicModule, ModalController } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { filter, firstValueFrom, timeout } from 'rxjs';
import { AcceptedNewLegalText, CgvSelectors, LoadCgvList, UserProfileSelector } from '@smatchy/core';
import {
  ATextPutAgainToTheMember,
  THE_WORDS_OF_THE_WINDOW,
  TheWordsOfTheWindow,
} from '@smatchy/domains/cgv/domain/a-new-version-of-the-terms-or-the-policy-is-put-to-the-member';
import { LanguageService } from '../../../../../core/services/language.service';
import { captureException } from '../../../../../tracing';

/** The dismissal home.component accepts once the API has recorded the tick. */
export const LEGAL_TEXT_AGREED_ROLE = 'legal-text-agreed';
/** The other one: the text could not be loaded, so it is asked again next launch. */
export const LEGAL_TEXT_POSTPONED_ROLE = 'legal-text-postponed';

/**
 * A new version of the terms of use or of the privacy policy, its full text,
 * and a box to tick before going on. Built on the window of the sale
 * conditions, which it follows on screen.
 */
@Component({
  selector: 'smatchy-term-legal-text-update',
  templateUrl: './term-legal-text-update.component.html',
  styleUrls: ['./term-legal-text-update.component.scss'],
  imports: [IonicModule, TranslateModule, CommonModule, FormsModule],
})
export class TermLegalTextUpdateModalComponent implements OnInit {
  @Input({ required: true }) text!: ATextPutAgainToTheMember;

  words!: TheWordsOfTheWindow;
  ticked = false;
  loading = false;
  submitFailed = false;
  failedToLoad = false;
  policyHtml: SafeHtml | null = null;
  /** Shown in a chip next to the title so the member knows which text they accept. */
  version: string | null = null;
  private userId: string | null = null;

  /**
   * Same limit as the legal text screen, for the same reason: without one, an
   * API with no record for the text leaves a window that can never be ticked
   * and never be closed, and the member cannot use the application at all.
   * After it, the member may close and is asked again at the next launch.
   */
  private static readonly WAIT_MS = 8000;

  constructor(
    private readonly _sanitizer: DomSanitizer,
    private readonly _store: Store,
    private readonly _modalCtrl: ModalController,
    private readonly _languageService: LanguageService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.words = THE_WORDS_OF_THE_WINDOW[this.text];
    this.userId = this._store.selectSnapshot(UserProfileSelector.profile)?.userId ?? null;

    this._store.dispatch(
      new LoadCgvList({ page: 1, limit: 1, type: this.text, sortBy: 'updatedAt', sortOrder: 'desc' }),
    );

    const version = await firstValueFrom(
      this._store.select(CgvSelectors.lastVersionOf(this.text)).pipe(
        filter((v) => !!v),
        timeout(TermLegalTextUpdateModalComponent.WAIT_MS),
      ),
    ).catch(() => null);

    const english = this._languageService.getCurrentLang() === 'en';
    const body = english
      ? version?.contentEn || version?.content || version?.contentFr
      : version?.contentFr || version?.content || version?.contentEn;

    if (!body) {
      this.failedToLoad = true;
      return;
    }
    this.policyHtml = this._sanitizer.bypassSecurityTrustHtml(body);
    this.version = version?.version ?? null;
  }

  async onSubmit(): Promise<void> {
    if (!this.userId || !this.ticked) {
      return;
    }
    this.loading = true;
    this.submitFailed = false;
    try {
      // firstValueFrom so a refusal from the API reaches the catch: a bare
      // await on dispatch resolves at once, which closed the sale conditions'
      // window whether anything was recorded or not.
      await firstValueFrom(this._store.dispatch(new AcceptedNewLegalText(this.text, this.userId)));
      await this._modalCtrl.dismiss(undefined, LEGAL_TEXT_AGREED_ROLE);
    } catch (error) {
      captureException(error);
      this.submitFailed = true;
    } finally {
      this.loading = false;
    }
  }

  later(): void {
    void this._modalCtrl.dismiss(undefined, LEGAL_TEXT_POSTPONED_ROLE);
  }
}
